import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { fetchSubscribers, type Subscriber } from "../lib/api";
import { DetailDrawer } from "../components/DetailDrawer";
import { countryLabel, countryMatches } from "../lib/countries";
import { ammanStamp, relativeTime } from "../lib/dates";
import { roleCan, useRole } from "../lib/roles";
import { DataTable } from "../components/DataTable";
import { PageSkeleton } from "../components/PageSkeleton";
import { Bidi, PageHeader, Surface, Tag } from "../components/primitives";
import { AccessDenied } from "./NewsletterEditorPage";

export function SubscribersPage() {
  const { role } = useRole();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const subs = useQuery({
    queryKey: ["subscribers", ""],
    queryFn: () => fetchSubscribers("", 100),
  });
  if (!roleCan(role, "subscribers.view")) return <AccessDenied need="Admins" />;

  const columns = useMemo<ColumnDef<Subscriber>[]>(() => {
    const cols: ColumnDef<Subscriber>[] = [
      { accessorKey: "email", header: "Email", cell: (c) => (
        <Bidi>{c.getValue<string>() ?? "—"}</Bidi>
      ) },
      // "United Arab Emirates" wrapped onto three lines and made its row twice
      // the height of its neighbours — a ragged column of rows reads worse
      // than a uniformly tall one.
      { accessorKey: "country", header: "Country",
        cell: (c) => {
          const label = countryLabel(c.getValue<string>());
          return <span className="ui-cell-1line" title={label}>{label}</span>;
        } },
      { id: "topics", header: "Topics",
        cell: (c) => {
          const list = c.row.original.interests ?? [];
          if (list.length === 0) return "—";
          // Three topics wrapped onto a second line and took the whole table
          // with them. Two and a count say the same thing on one line.
          const shown = list.slice(0, 2).join(", ");
          const rest = list.length > 2 ? ` +${list.length - 2}` : "";
          return <span className="ui-cell-1line" title={list.join(", ")}>{shown}{rest}</span>;
        } },
      // The exact stamp stays a hover away; the column itself reads at a
      // glance and fits on one line, which is what set the row height.
      { accessorKey: "synced_at", header: "Joined",
        cell: (c) => (
          <span className="tabnum ui-cell-1line" title={ammanStamp(c.getValue<string>())}>
            {relativeTime(c.getValue<string>())}
          </span>
        ) },
    ];
    return cols;
  }, []);

  if (subs.isLoading) return <PageSkeleton />;

  const data = subs.data?.rows ?? [];
  const selected = data.find((s) => s.id === selectedId) ?? null;
  const countries = new Set(data.map((s) => s.country).filter(Boolean)).size;
  const total = subs.data?.total ?? data.length;
  const live = (subs.data?.source ?? "").startsWith("supabase");

  return (
    <div style={{ display: "grid", gap: "var(--ui-space-5)" }}>
      <PageHeader
        title="Subscribers"
        meta={live
          ? "Live signups from the Altibbi newsletter form."
          : "Demo list — connect Supabase and run newsletter_sync to load the live list."}
      />

      <Surface style={{ overflow: "hidden" }}>
        <div className="nl-stat-row">
          <div className="nl-stat-mini">
            <div className="nl-stat-mini-label">In this list</div>
            <div className="nl-stat-mini-value tabnum">{total}</div>
          </div>
          <div className="nl-stat-mini">
            <div className="nl-stat-mini-label">Countries</div>
            <div className="nl-stat-mini-value tabnum">{countries}</div>
          </div>
          <div className="nl-stat-mini">
            <div className="nl-stat-mini-label">Showing</div>
            <div className="nl-stat-mini-value tabnum">{data.length}</div>
          </div>
        </div>
      </Surface>

      <div className="nl-workspace">
        <Surface className="nl-table-surface">
          <DataTable
            data={data}
            columns={columns}
            selectedId={selected?.id ?? null}
            getRowId={(row) => row.id}
            onRowClick={(row) => setSelectedId(row.id)}
            searchPlaceholder="Search email, country, or topic…"
            globalFilterFn={(row, term) => {
              const n = term.toLowerCase();
              return Boolean(
                row.email?.toLowerCase().includes(n) ||
                countryMatches(row.country, n) ||
                row.interests?.some((i) => i.toLowerCase().includes(n)),
              );
            }}
            exportName="subscribers"
            ariaLabel="Subscribers"
          />
        </Surface>
        {selected && (
          <DetailDrawer label="Subscriber" onClose={() => setSelectedId(null)}>
            <SubscriberExplorer person={selected} />
          </DetailDrawer>
        )}
      </div>
    </div>
  );
}

function SubscriberExplorer({ person }: { person: Subscriber | null }) {
  if (!person) {
    return (
      <aside className="nl-detail-panel">
        <div className="nl-detail-panel-body">
          <p style={{ color: "var(--c-t3)", margin: 0 }}>Select a row to see topics and activity.</p>
        </div>
      </aside>
    );
  }

  return (
    <aside className="nl-detail-panel">
      <div className="nl-detail-panel-head">
        <div className="nl-stat-mini-label">Subscriber</div>
        <div style={{ fontWeight: 600, fontSize: "var(--ui-text-section)", marginTop: 6, color: "var(--c-t1)", wordBreak: "break-all" }}>
          <Bidi>{person.email ?? "—"}</Bidi>
        </div>
        {person.name && <div style={{ fontSize: "var(--ui-text-control)", color: "var(--c-t3)", marginTop: 4 }}><Bidi>{person.name}</Bidi></div>}
      </div>
      <div className="nl-detail-panel-body">
        <dl className="nl-kv">
          <div><dt>Country</dt><dd>{countryLabel(person.country)}</dd></div>
          <div><dt>Language</dt><dd>{person.locale ?? "—"}</dd></div>
          <div><dt>Joined</dt><dd className="tabnum">{ammanStamp(person.synced_at)}</dd></div>
        </dl>
        <div>
          <div className="nl-stat-mini-label">Topics</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
            {(person.interests ?? []).length === 0 && <span style={{ color: "var(--c-t3)", fontSize: "var(--ui-text-control)" }}>No topics selected</span>}
            {(person.interests ?? []).map((i) => <Tag key={i}>{i}</Tag>)}
          </div>
        </div>
        <div>
          <div className="nl-stat-mini-label">Newsletter activity</div>
          <dl className="nl-kv">
            <div><dt>Last received</dt><dd className="tabnum">{ammanStamp(person.last_received)}</dd></div>
            <div><dt>Last opened</dt><dd className="tabnum">{ammanStamp(person.last_opened)}</dd></div>
            <div><dt>Last clicked</dt><dd className="tabnum">{ammanStamp(person.last_clicked)}</dd></div>
            <div><dt>Unsubscribed from</dt><dd>{person.unsubscribed_from ?? "—"}</dd></div>
          </dl>
        </div>
      </div>
    </aside>
  );
}
